import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import './VitalsTabs.css';

const parseVitals = (vitalsRaw) => {
  const tokens = [];
  const pushToken = (value) => {
    const token = String(value ?? '').trim().toUpperCase();
    if (!token) return;
    tokens.push(token);
  };

  if (Array.isArray(vitalsRaw)) {
    vitalsRaw.forEach(pushToken);
  } else if (vitalsRaw && typeof vitalsRaw === 'object') {
    Object.entries(vitalsRaw).forEach(([key, enabled]) => {
      if (enabled) pushToken(key);
    });
  } else {
    const raw = String(vitalsRaw ?? '')
      .toUpperCase()
      .replace(/[|/]/g, ',');
    raw.split(',').forEach(pushToken);
  }

  const normalized = new Set(tokens.flatMap((t) => t.split(/\s+/)).filter(Boolean));
  const joined = tokens.join(' ');

  const hasBP = normalized.has('BP') || joined.includes('BLOOD PRESSURE') || normalized.has('BLOOD_PRESSURE');
  const hasBG = normalized.has('BG') || joined.includes('BLOOD GLUCOSE') || normalized.has('BLOOD_GLUCOSE') || normalized.has('GLUCOSE');
  const hasWT = normalized.has('WT') || normalized.has('W') || joined.includes('WEIGHT');

  return { bp: hasBP, bg: hasBG, wt: hasWT };
};

const VitalsTabs = ({ practiceId, patientId, vitals, active, mode = 'measurement', className = '' }) => {
  const navigate = useNavigate();

  const available = useMemo(() => parseVitals(vitals), [vitals]);

  const tabs = useMemo(() => {
    const base = `/practices/${practiceId}/patients/${patientId}/measurements`;
    const items = [];
    if (available.bp) {
      items.push({
        key: 'blood-pressure',
        label: mode === 'chart' ? 'Blood Pressure Chart' : 'Blood Pressure',
        path: mode === 'chart' ? `${base}/blood-pressure/chart` : `${base}/blood-pressure`
      });
    }
    if (available.bg) {
      items.push({
        key: 'blood-glucose',
        label: mode === 'chart' ? 'Blood Glucose Chart' : 'Blood Glucose',
        path: mode === 'chart' ? `${base}/blood-glucose/chart` : `${base}/blood-glucose`
      });
    }
    if (available.wt) {
      items.push({
        key: 'weight',
        label: mode === 'chart' ? 'Weight Graphs' : 'Weight',
        path: mode === 'chart' ? `${base}/weight/chart` : `${base}/weight`
      });
    }
    return items;
  }, [available.bp, available.bg, available.wt, practiceId, patientId, mode]);

  if (!tabs.length) return null;

  return (
    <div className={`res-vitals-tabs ${className}`.trim()}>
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          className={`res-vitals-tab ${active === t.key ? 'is-active' : ''}`.trim()}
          onClick={() => navigate(t.path)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
};

VitalsTabs.propTypes = {
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  vitals: PropTypes.string,
  active: PropTypes.oneOf(['blood-pressure', 'blood-glucose', 'weight']).isRequired,
  mode: PropTypes.oneOf(['measurement', 'chart']),
  className: PropTypes.string
};

export default VitalsTabs;

